import type {FC, PropsWithChildren} from "react"

import Image, {ImageProps} from "next/image"

import {CourseFragment} from "generated/datocms"

import {Anchor} from "components/Anchor"

import {UserHeaderWithLogo} from "components/UserHeader"

import coursesBuildup from "public/images/courses-buildup.png"
import coursesCheckup from "public/images/courses-checkup.png"
import coursesDrillDown from "public/images/courses-drill-down.png"
import coursesTranscend from "public/images/courses-transcend.png"
import arrowDown from "public/images/arrow-down.png"
import cn from "classnames"
import {Course, CoursesList} from "lib/scheduler"
import {CourseState} from "lib/scheduler/enums"

export interface CoursesProgressPageProps {
  userId?: string | null
  isAuthenticated: boolean
  courses: CourseFragment[]
  ongoingCourseId: string | null
  knownCourses: CoursesList | null
}

const StepImage = ({src, width = 100, height = 100, className}: {
  src: ImageProps["src"],
  width?: number,
  height?: number
  className?: string
}) => {
  return (
    <div className={["flex justify-center align-middle mb-4", className].filter(Boolean).join(" ")}>
      <Image src={src} alt="" width={width} height={height} />
    </div>
  )
}

const ArrowDownImage = () => {
  return (
    <div className="flex justify-center align-middle mb-5">
      <Image src={arrowDown} alt="" />
    </div>
  )
}

const BlockAnchor: FC<PropsWithChildren<{href: string}>> = ({children, href}) => {
  return (
    <Anchor
      href={href}
      className="no-underline h-full block"
    >{children}
    </Anchor>
  )
}

const Container: FC<PropsWithChildren<{active?: boolean}>> = ({children, active = true}) => {
  return <div className={cn("flex flex-col align-middle justify-center", {"opacity-50": !active})}>{children}</div>
}

const ComponentsContainer: FC<PropsWithChildren> = ({children}) => {
  return <div className="inline-flex flex-wrap align-middle justify-center mb-4">{children}</div>
}

// const ComponentIndex: FC<{id: string, color: string}> = ({id, color}) => {
//   return <span className={`w-7 h-7 rounded-full border bg-${color} flex
// align-middle justify-center`}>{id}</span>
// }

// TODO: angle
const Component: FC<{title: string, course?: Course}> = ({
  title,
  course
}) => {
  const content = (
    <div className="p-3 m-2.5 border relative bg-black-200">
      <p className="m-4">{title}</p>
    </div>
  )
  if (course?.state && [CourseState.NOT_STARTED, CourseState.ONGOING].includes(course.state)) {
    return <BlockAnchor href={`/courses/${course.slug}`}>{content}</BlockAnchor>
  }

  return content
}

const drillDownComponents = [
  {
    id: "d1",
    title: "Guilt and Regret",
    color: "dirty-blue",
    slug: "guilt-and-regret",
  }, {
    id: "d2",
    title: "Fear and Anxiety",
    color: "dirty-blue",
    slug: "fear-and-anxiety",
  }, {
    id: "d3",
    title: "Depression",
    color: "dirty-blue",
    slug: "depression",
  }, {
    id: "d4",
    title: "Burnout",
    color: "dirty-blue",
    slug: "burnout",
  }, {
    id: "d5",
    title: "Internal Critics",
    color: "dirty-blue",
    slug: "internal-critics",
  }, {
    id: "d6",
    title: "Pleaser No More",
    color: "dirty-blue",
    slug: "pleaser-no-more",
  }
]

const transcendComponents = [
  {
    id: "t1",
    title: "Healthy relationships",
    color: "orange-attention",
    slug: "",
  }, {
    id: "t2",
    title: "Non-violent communication",
    color: "orange-attention",
    slug: "",
  }, {
    id: "t3",
    title: "Creativity growth",
    color: "orange-attention",
    slug: "",
  }, {
    id: "t4",
    title: "Mindfulness",
    color: "orange-attention",
    slug: "",
  }, {
    id: "t5",
    title: "Vitalty rejuvination",
    color: "orange-attention",
    slug: "",
  }
]

function courseIsAvailable(course: Course) {
  return ["NOT_STARTED", "ONGOING", "READY_TO_FINISH"].includes(course.state)
}

function courseCompleted(course: Course) {
  return course.state === "COMPLETED"
}
function courseCompletedFail(course: Course) {
  return course.state === "COMPLETED_FAIL"
}

const YourAreHere: FC = () => (
  <div className="text-center mb-5">
    <div className="px-2.5 py-3 text-center inline-block bg-celadon">
      You are here
    </div>
  </div>
)

const CourseStatus: FC<{course?: Course}> = ({course}) => {
  if (!course) return null

  if (courseCompleted(course)) {
    return <span className="text-xl text-center mb-5 text-green-success">Completed!</span>
  }
  if (courseCompletedFail(course)) {
    return <span className="text-xl text-center mb-5 text-orange-attention">Failed 😔</span>
  }

  return null
}

export const CoursesProgressPage: FC<CoursesProgressPageProps> = ({
  isAuthenticated,
  knownCourses,
//   courses,
//   ongoingCourseId
}) => {
  const checkUpCourse = knownCourses?.find(c => c.slug === "check-up")
  const buildUpCourse = knownCourses?.find(c => c.slug === "build-up")
  const drillDownIsAvailable = knownCourses?.some(
    c => c.course_group === "drill-down" && ["NOT_STARTED", "ONGOING", "READY_TO_FINISH"].includes(c.state)
  )
  const checkUpIsAvailable = !knownCourses
  || !knownCourses.length
  || (!!checkUpCourse && courseIsAvailable(checkUpCourse))
  const buildUpIsAvailable = !!buildUpCourse && courseIsAvailable(buildUpCourse)

  const checkUp = (
    <>
      <StepImage src={coursesCheckup} />
      <h5 className="text-center mb-[5px]">Check up</h5>
      {checkUpIsAvailable && <YourAreHere />}
      <CourseStatus course={checkUpCourse} />
    </>
  )
  const buildUp = (
    <>
      <StepImage src={coursesBuildup} />
      <h5 className="text-center mb-[5px]">Build up</h5>
      {buildUpIsAvailable && <YourAreHere />}
      <CourseStatus course={buildUpCourse} />
    </>
  )
  return (
    <div className="pb-5">
      <UserHeaderWithLogo {...{isAuthenticated}} className="pb-0 mb-14" />

      <div className="flex flex-col justify-center">

        <Container active={checkUpIsAvailable}>
          {checkUpIsAvailable ? (<BlockAnchor href="/courses/check-up">{checkUp}</BlockAnchor>) : checkUp}
          <ArrowDownImage />
        </Container>

        <Container active={buildUpIsAvailable}>
          {buildUpIsAvailable ? (<BlockAnchor href="/courses/build-up">{buildUp}</BlockAnchor>) : buildUp}
          <ArrowDownImage />
        </Container>

        <Container active={drillDownIsAvailable}>
          <StepImage src={coursesDrillDown} />
          <h5 className="text-center mb-[5px]">Drill down</h5>

          <ComponentsContainer>
            {drillDownComponents.map(
              c => (
                <Component
                  key={c.id}
                  course={knownCourses?.find(k => k.slug === c.slug)}
                  {...c}
                />
              )
            )}
          </ComponentsContainer>

          <ArrowDownImage />
        </Container>

        <Container active={false}>
          <StepImage src={coursesTranscend} />
          <h5 className="text-center mb-[5px]">Transcend</h5>

          <ComponentsContainer>
            {transcendComponents.map(c => <Component key={c.id} {...c} />)}
          </ComponentsContainer>
        </Container>

      </div>

      <div className="bg-dirty-blue" />
      <div className="bg-orange-attention" />

    </div>
  )
}
